import { hasWebsiteIcon } from "@/icons/icon-catalogue";
import {
  formattingMenuId,
  type MenuFormatting,
  type MenuFormattingSite,
} from "@/cms/menu-formatting";
import type { CmsPage, HeaderMenuSettings } from "@/cms/models";
import type { NavigationIcon, NavigationItem } from "@/cms/classic";
import { getPageNavigation } from "@/utils/page-navigation";
import { isLookSystem } from "@/utils/look-gate";
import { menuFormattingsInDark } from "@/utils/own-dark-fields";
import { headerSurfacesInDark } from "@/utils/header-colours";
import type { HeaderTop } from "@/utils/header-state";

export type HeaderTypeName =
  | "DefaultHeader"
  | "TransparentHeader"
  | "FloatingHeader"
  | "ClassicHeader";

/** The part of `layout.header` (root or page) that decides the menu. */
export interface HeaderNavigationSettings {
  type?: string;
  menuId?: string;
  navigation?: NavigationItem[];
  menu?: HeaderMenuSettings;
  classic?: { navigation?: NavigationItem[]; compact?: boolean };
}

export interface ResolvedLink {
  href: string;
  /** Rendered as a plain anchor instead of a client-side route. */
  external: boolean;
  newTab: boolean;
}

export interface NavEntry {
  /** Unique within the tree; item ids are not (Klassisk data repeats them). */
  key: string;
  id: string;
  kind: "page" | "link" | "group" | "heading";
  label: string;
  /** Set on page and link items. A level-1 page or link with children is a clickable group. */
  link?: ResolvedLink;
  description?: string;
  icon?: NavigationIcon;
  iconId?: string | null;
  devices?: "m" | "d";
  children: NavEntry[];
}

export interface MenuOptions {
  barItems: number;
  barAlignment?: "left" | "right";
  hoverEffect?: "chevron" | "underline" | "background" | "none";
  hoverMarker?: "slashes";
  style: "drawer" | "slideDown" | "fullscreen";
  side: "left" | "right";
  panelWidth?: "small" | "medium" | "wide";
  groups: "accordion" | "columns";
  submenus: "dropdown" | "mega";
  button: "icon" | "iconText" | "text";
  hideButtonWhenEmpty: boolean;
  overflow: boolean;
}

export type NavigationSource =
  | "page"
  | "page-classic"
  | "root"
  | "root-classic"
  | "derived";

export interface ResolvedNavigation {
  type: HeaderTypeName;
  websiteFormatting?: MenuFormatting;
  menuFormatting?: MenuFormatting;
  /** Set on the look system only: the formatting's palette and role references are colours there. */
  colourReferences?: boolean;
  source: NavigationSource;
  options: MenuOptions;
  /** The resolved tree for both devices. */
  items: NavEntry[];
  /** Desktop bar, before client-side overflow measuring. */
  bar: NavEntry[];
  /** Desktop panel: the top-level items after the bar. */
  panel: NavEntry[];
  /** Mobile panel: bar items first, then the rest. */
  mobile: NavEntry[];
}

export const navigationIcons: NavigationIcon[] = [
  "search",
  "home",
  "mail",
  "phone",
  "user",
  "calendar",
  "star",
  "check",
  "list",
  "location",
];

const headerTypes: HeaderTypeName[] = [
  "DefaultHeader",
  "TransparentHeader",
  "FloatingHeader",
  "ClassicHeader",
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const oneOf = <T extends string>(value: unknown, values: readonly T[]) =>
  values.includes(value as T) ? (value as T) : undefined;

const text = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

/**
 * The gate for the new menu markup: the effective header (root merged with the page) has
 * `navigation` or `menu`. Without either, every header renders exactly what it did before.
 */
export function navigationEnabled(
  header: HeaderNavigationSettings | undefined | null,
) {
  return Array.isArray(header?.navigation) || isRecord(header?.menu);
}

export function headerType(type: unknown): HeaderTypeName {
  return oneOf(type, headerTypes) ?? "DefaultHeader";
}

export function pagePath(slug: string | undefined) {
  return `/${(slug ?? "").split("/").filter(Boolean).join("/")}`;
}

export function linkFor(url: string, newTab = false): ResolvedLink {
  return {
    href: url,
    external: /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(url),
    newTab,
  };
}

/** Today's menu rules for a page: an external redirect when set, otherwise `/${slug}`. */
export function pageLink(page: CmsPage, openInNewTab?: boolean): ResolvedLink {
  const external = !!(page.redirectToExternal && page.externalUrl);
  const newTab = openInNewTab ?? (external && !!page.openInNewTab);
  return external
    ? { ...linkFor(page.externalUrl!), newTab }
    : { href: pagePath(page.slug), external: false, newTab };
}

const trimSlashes = (slug: string) => slug.replace(/^\/+|\/+$/g, "");

/** By id, then by the stored slug, which survives an editor that reassigns page ids. */
export function findMenuPage(
  pages: CmsPage[],
  pageId: unknown,
  slug: unknown,
): CmsPage | undefined {
  const candidates = pages.filter(
    (page) => page.type !== "footer" && page.type !== "article",
  );
  const byId =
    typeof pageId === "string" && pageId
      ? candidates.find((page) => page.id === pageId)
      : undefined;
  if (byId || typeof slug !== "string") return byId;
  return (
    candidates.find((page) => page.slug === slug) ??
    candidates.find(
      (page) => trimSlashes(page.slug ?? "") === trimSlashes(slug),
    )
  );
}

type Level = 1 | 2 | 3;

function itemKind(
  item: NavigationItem,
  level: Level,
): NavEntry["kind"] | undefined {
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const kind =
    oneOf(item.kind, ["page", "link", "group", "heading"] as const) ??
    (item.pageId
      ? "page"
      : item.url
        ? "link"
        : hasChildren
          ? level === 1
            ? "group"
            : "heading"
          : undefined);
  if (kind === "page" || kind === "link") return kind;
  if (!kind || level === 3) return undefined;
  return level === 1 ? "group" : "heading";
}

function resolveItems(
  items: unknown,
  level: Level,
  pages: CmsPage[],
  parentKey: string,
): NavEntry[] {
  if (!Array.isArray(items)) return [];
  return items.flatMap((raw, index): NavEntry[] => {
    if (!isRecord(raw)) return [];
    const item = raw as unknown as NavigationItem;
    const kind = itemKind(item, level);
    if (!kind) return [];
    const key = `${parentKey}${parentKey ? "-" : ""}${index}`;
    const common = {
      key,
      id: typeof item.id === "string" && item.id ? item.id : key,
      kind,
      description: text(item.description),
      icon: oneOf(item.icon, navigationIcons),
      iconId: hasWebsiteIcon(item.iconId) ? item.iconId : undefined,
      devices: oneOf(item.devices, ["m", "d"] as const),
    };
    const children = (childLevel: Level) =>
      resolveItems(item.children, childLevel, pages, key);
    if (kind === "page" || kind === "link") {
      let link: ResolvedLink | undefined;
      let label = text(item.label);
      if (kind === "page") {
        const page = findMenuPage(pages, item.pageId, item.slug);
        if (!page) return [];
        link = pageLink(page, item.openInNewTab);
        label ??= text(page.menuTitle) ?? text(page.browserTitle);
      } else {
        const url = text(item.url);
        if (!url) return [];
        link = linkFor(url, !!item.openInNewTab);
      }
      if (!label) return [];
      return [
        {
          ...common,
          label,
          link,
          children: level === 1 ? children(2) : [],
        },
      ];
    }
    const label = text(item.label);
    const nested = children(level === 1 ? 2 : 3);
    if (!label || !nested.length) return [];
    return [{ ...common, label, children: nested }];
  });
}

function derivedItems(type: HeaderTypeName, pages: CmsPage[]) {
  const menuPages =
    type === "FloatingHeader"
      ? (() => {
          const { primary, sidebar } = getPageNavigation(pages);
          return [
            ...primary,
            ...sidebar.filter((page) => !primary.includes(page)),
          ];
        })()
      : pages.filter(
          (page) =>
            page.type !== "footer" &&
            page.type !== "article" &&
            page.includeInMenu,
        );
  return menuPages.map(
    (page): NavigationItem => ({
      id: `menu-${page.id}`,
      kind: "page",
      pageId: page.id,
      slug: page.slug,
    }),
  );
}

function classicMenuIds(items: NavigationItem[]) {
  const used = new Set<string>();
  const copy = (values: NavigationItem[], parent = ""): NavigationItem[] =>
    values.filter(isRecord).map((item, index) => {
      const path = parent ? `${parent}-${index}` : String(index);
      let id = item.id && !used.has(item.id) ? item.id : `menu-${path}`;
      let suffix = 2;
      while (used.has(id)) id = `menu-${path}-${suffix++}`;
      used.add(id);
      return {
        ...item,
        id,
        ...(Array.isArray(item.children)
          ? { children: copy(item.children, path) }
          : {}),
      };
    });
  return copy(items);
}

function selectSource(
  type: HeaderTypeName,
  root: HeaderNavigationSettings,
  page: HeaderNavigationSettings,
): { source: NavigationSource; items?: NavigationItem[] } {
  const classic = type === "ClassicHeader";
  if (Array.isArray(page.navigation))
    return { source: "page", items: page.navigation };
  if (classic && Array.isArray(page.classic?.navigation))
    return { source: "page-classic", items: page.classic.navigation };
  if (Array.isArray(root.navigation))
    return { source: "root", items: root.navigation };
  if (classic && Array.isArray(root.classic?.navigation))
    return { source: "root-classic", items: root.classic.navigation };
  return { source: "derived" };
}

function barCount(value: unknown) {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.floor(value))
    : undefined;
}

export function menuOptions(
  type: HeaderTypeName,
  menu: HeaderMenuSettings | undefined,
  pages: CmsPage[],
): MenuOptions {
  const settings = isRecord(menu) ? menu : {};
  const style =
    oneOf(settings.style, ["drawer", "slideDown", "fullscreen"] as const) ??
    (type === "ClassicHeader" ? "fullscreen" : "drawer");
  const defaultBarItems =
    type === "ClassicHeader"
      ? Number.MAX_SAFE_INTEGER
      : type === "FloatingHeader"
        ? getPageNavigation(pages).primary.length
        : 0;
  return {
    barItems: barCount(settings.barItems) ?? defaultBarItems,
    barAlignment: oneOf(settings.barAlignment, ["left", "right"] as const),
    hoverEffect: oneOf(settings.hoverEffect, [
      "chevron",
      "underline",
      "background",
      "none",
    ] as const),
    style,
    side: oneOf(settings.side, ["left", "right"] as const) ?? "right",
    panelWidth: oneOf(settings.panelWidth, [
      "small",
      "medium",
      "wide",
    ] as const),
    groups:
      oneOf(settings.groups, ["accordion", "columns"] as const) ??
      (style === "drawer" || type === "ClassicHeader"
        ? "accordion"
        : "columns"),
    submenus:
      oneOf(settings.submenus, ["dropdown", "mega"] as const) ?? "dropdown",
    button:
      oneOf(settings.button, ["icon", "iconText", "text"] as const) ?? "icon",
    hideButtonWhenEmpty:
      typeof settings.hideButtonWhenEmpty === "boolean"
        ? settings.hideButtonWhenEmpty
        : type === "ClassicHeader",
    overflow: typeof settings.overflow === "boolean" ? settings.overflow : true,
  };
}

/**
 * What an opened group lists. A group header only opens and closes, so a group with its own page lists that page
 * first - unless one of its links already goes there, as generated Klassisk menus repeat the parent as a child.
 */
export function groupChildren(entry: NavEntry): NavEntry[] {
  const link = entry.link;
  if (!link || !entry.children.length) return entry.children;
  if (entry.children.some((child) => child.link?.href === link.href))
    return entry.children;
  const own: NavEntry = {
    key: `${entry.key}-own`,
    id: entry.id,
    kind: entry.kind,
    label: entry.label,
    link,
    children: [],
  };
  return [own, ...entry.children];
}

/** Drops items for the other device; a group or heading left without children goes too. */
export function forDevice(items: NavEntry[], hidden: "m" | "d"): NavEntry[] {
  return items.flatMap((item) => {
    if (item.devices === hidden) return [];
    const children = forDevice(item.children, hidden);
    if (!item.link && !children.length) return [];
    return [{ ...item, children }];
  });
}

/**
 * Resolves the menu a header shows, following contract precedence: the page's own `navigation`,
 * the page's own Klassisk `classic.navigation`, the root `navigation`, the root Klassisk
 * `classic.navigation`, and finally the pages list each header type derives today.
 * Returns undefined when the gate (`navigationEnabled`) is not met. `colours` are the header's own
 * (`headers[].colours`), which decide with the top it is drawn with (`top`, HeaderStateController's) whether the
 * menu's own text takes its dark version.
 */
export function resolveNavigation(input: {
  pages: CmsPage[];
  site?: MenuFormattingSite | null;
  type: unknown;
  root?: HeaderNavigationSettings | null;
  page?: HeaderNavigationSettings | null;
  colours?: unknown;
  top?: HeaderTop;
}): ResolvedNavigation | undefined {
  const root: HeaderNavigationSettings = isRecord(input.root) ? input.root : {};
  const page: HeaderNavigationSettings = isRecord(input.page) ? input.page : {};
  if (!navigationEnabled(root) && !navigationEnabled(page)) return undefined;
  const pages = Array.isArray(input.pages) ? input.pages : [];
  const type = headerType(input.type);
  const { source, items: rawItems } = selectSource(type, root, page);
  const items = resolveItems(
    rawItems
      ? source === "root-classic" || source === "page-classic"
        ? classicMenuIds(rawItems)
        : rawItems
      : derivedItems(type, pages),
    1,
    pages,
    "",
  );
  const options = menuOptions(
    type,
    {
      ...(isRecord(root.menu) ? root.menu : {}),
      ...(isRecord(page.menu) ? page.menu : {}),
    },
    pages,
  );
  const compact =
    type === "ClassicHeader" &&
    !!({ ...root.classic, ...page.classic } as { compact?: boolean }).compact;
  const desktop = forDevice(items, "m");
  const barSize = compact ? 0 : options.barItems;
  const bar = desktop.slice(0, barSize);
  const barKeys = new Set(bar.map((item) => item.key));
  const mobileItems = forDevice(items, "d");
  const formatting = menuFormattingsInDark(
    input.site?.theme,
    input.site?.menuTypography,
    input.site?.menuFormatting?.[
      formattingMenuId(
        input.site,
        (source === "page" || source === "page-classic") && !page.menuId
          ? page
          : root,
      )
    ],
    headerSurfacesInDark(
      input.site?.theme,
      { type, config: root, page, top: input.top },
      input.colours,
    ),
  );
  return {
    type,
    websiteFormatting: formatting.website,
    menuFormatting: formatting.menu,
    ...(isLookSystem(input.site?.theme) ? { colourReferences: true } : {}),
    source,
    options: compact ? { ...options, barItems: 0 } : options,
    items,
    bar,
    panel: desktop.slice(barSize),
    mobile: [
      ...mobileItems.filter((item) => barKeys.has(item.key)),
      ...mobileItems.filter((item) => !barKeys.has(item.key)),
    ],
  };
}

export const menuHoverMarker = (subdomain: unknown): "slashes" | undefined =>
  subdomain === "claus-borg" ? "slashes" : undefined;
